import {
  entriesOverlap,
  type ListTimeEntriesInput,
  type TimeEntryDay,
  type TimeEntryDto,
  weekStart,
} from '@fos/shared/contracts/time';
import { addDaysLocal, todayLocal } from '@fos/shared/time';
import { keepPreviousData } from '@tanstack/react-query';
import { CircleCheck, Clock, Copy, Lock, MoreHorizontal, Pencil, Trash2, TriangleAlert } from 'lucide-react';
import { type ReactNode, useMemo, useState } from 'react';
import { useSession } from '../../../lib/auth';
import { cn } from '../../../lib/cn';
import { formatClock, formatMoneyAuto, formatRate } from '../../../lib/format';
import { useScope } from '../../../lib/scope';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { Avatar } from '../../../ui/Avatar';
import { Button, IconButton } from '../../../ui/Button';
import { ClientChip, ProjectChip } from '../../../ui/EntityLabel';
import { Menu, MenuContent, MenuItem, MenuSeparator, MenuTrigger } from '../../../ui/Menu';
import { Mono } from '../../../ui/Mono';
import { Section } from '../../../ui/Section';
import { EmptyState, ErrorState, ListSkeleton } from '../../../ui/States';
import { Tooltip } from '../../../ui/Tooltip';
import { TOUCH_BUTTON } from '../../mobile';
import { useArchiveEntry } from '../hooks/useTimeMutations';
import { dayLabel, weekLabel } from '../time-format';
import { AddTimeDialog } from './AddTimeDialog';

/**
 * Timesheet w stylu Clockify (Figma 06 Czas / Wpisy, 3024:2282): tygodnie („Ten tydzień”, „Poprzedni tydzień”)
 * i dni („Dziś · nd 4.10”) z sumami w mono, wiersz: opis, kropka i nazwa klienta, projekt, od–do, czas, menu „⋯”
 * (Edytuj, Duplikuj, Usuń z „Cofnij”). Klik w wiersz otwiera edycję. Nakładające się wpisy tej samej osoby
 * mają ostrzeżenie. Eksport dla innych widoków (profil klienta, projekt, zadanie): `<TimeEntryRows filter={…} />`.
 */

export type TimeEntryRowsFilter = Omit<ListTimeEntriesInput, 'limit'>;

/** Ile tygodni wstecz pokazuje lista bez własnego zakresu (i o ile rozszerza „Pokaż wcześniejsze”). */
const WEEKS_STEP = 4;

/** Wpisy nachodzące na inny wpis tej samej osoby tego samego dnia (ostrzeżenie w wierszu). */
function overlappingIds(days: TimeEntryDay[]): Set<string> {
  const out = new Set<string>();
  for (const day of days) {
    const list = day.entries;
    for (let i = 0; i < list.length; i++) {
      for (let j = i + 1; j < list.length; j++) {
        const a = list[i] as TimeEntryDto;
        const b = list[j] as TimeEntryDto;
        if (a.userId === b.userId && entriesOverlap(a, b)) {
          out.add(a.id);
          out.add(b.id);
        }
      }
    }
  }
  return out;
}

export function TimeEntryRows({
  filter = {},
  emptyTitle = 'Brak wpisów czasu',
  emptyDescription,
  emptyAction,
  showWeeks = true,
  framed = false,
  className,
}: {
  /** filtr listy (`time.list`): klient, projekt, zadanie, zakres dat, Moje / Zespół (domyślnie z menu) */
  filter?: TimeEntryRowsFilter;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyAction?: ReactNode;
  /** nagłówki tygodni (wyłącz w wąskich widokach) */
  showWeeks?: boolean;
  /**
   * Ekran Czas: każdy tydzień (i stan pusty, ładowanie, błąd) w osobnej białej sekcji (`ui/Section`). Bez tego — sama
   * lista do osadzenia w sekcji widoku nadrzędnego (profil klienta, projekt, karta zadania).
   */
  framed?: boolean;
  className?: string;
}) {
  const { scope: menuScope } = useScope();
  const access = useAccess();
  const today = todayLocal();
  const [extraSteps, setExtraSteps] = useState(0);
  const [editing, setEditing] = useState<TimeEntryDto | null>(null);
  const [duplicating, setDuplicating] = useState<TimeEntryDto | null>(null);
  const archive = useArchiveEntry();
  const session = useSession().data;
  const me = session?.authenticated ? session.user.id : null;

  const scope = filter.scope ?? menuScope;
  const ownRange = Boolean(filter.from || filter.to);
  const input: ListTimeEntriesInput = {
    ...filter,
    scope,
    ...(ownRange
      ? {}
      : {
          from: addDaysLocal(weekStart(today), -7 * (WEEKS_STEP - 1) - 7 * WEEKS_STEP * extraSteps),
          to: addDaysLocal(weekStart(today), 6),
        }),
  };
  const list = trpc.time.list.useQuery(input, { placeholderData: keepPreviousData });
  const data = list.data;
  const overlaps = useMemo(() => overlappingIds(data?.weeks.flatMap((w) => w.days) ?? []), [data]);
  const team = scope === 'team';
  const canEdit = access.can('time', 'edit');

  if (list.isPending) {
    const skeleton = <ListSkeleton rows={5} className={framed ? undefined : className} />;
    return framed ? <Section className={className}>{skeleton}</Section> : skeleton;
  }
  if (list.isError && !data) {
    const error = (
      <ErrorState
        className={framed ? undefined : className}
        onRetry={() => void list.refetch()}
        retrying={list.isFetching}
        description="Nie udało się wczytać wpisów czasu."
      />
    );
    return framed ? <Section className={className}>{error}</Section> : error;
  }
  if (!data) return null;

  if (data.weeks.length === 0) {
    const Box = framed ? Section : 'div';
    return (
      <Box className={className}>
        <EmptyState
          icon={Clock}
          title={emptyTitle}
          description={
            emptyDescription ??
            (ownRange ? 'W tym okresie nie ma wpisów.' : 'Dodaj pierwszy wpis: opis, klient, godziny od–do.')
          }
          action={emptyAction}
        />
        {!ownRange && (
          <div className="flex justify-center">
            <Button
              variant="ghost"
              size="s"
              className={TOUCH_BUTTON}
              onClick={() => setExtraSteps((n) => n + 1)}
              loading={list.isFetching}
            >
              Pokaż wcześniejsze
            </Button>
          </div>
        )}
      </Box>
    );
  }

  const Week = framed ? Section : 'section';
  return (
    <div className={cn('flex flex-col', framed ? 'gap-4 md:gap-6' : 'gap-10', className)}>
      {data.weeks.map((week) => (
        <Week key={week.weekStart} aria-label={weekLabel(week.weekStart, today)} className="flex flex-col">
          {showWeeks && (
            <div className="flex items-baseline justify-between gap-3 pb-3">
              <h2 className="text-body-m font-semibold text-fg">{weekLabel(week.weekStart, today)}</h2>
              <Mono className="text-body-s text-fg">{formatClock(week.totals.seconds)}</Mono>
            </div>
          )}
          <div className="flex flex-col gap-3">
            {week.days.map((day) => (
              <div key={day.date} className="flex flex-col">
                <div className="flex h-8 items-center justify-between gap-3 rounded-lg bg-surface px-4">
                  <h3 className="text-caption text-fg-muted">{dayLabel(day.date, today)}</h3>
                  <Mono className="text-caption text-fg-muted" aria-label={`Razem ${formatClock(day.totals.seconds)}`}>
                    {formatClock(day.totals.seconds)}
                  </Mono>
                </div>
                <ul className="flex flex-col">
                  {day.entries.map((e) => (
                    <EntryRow
                      key={e.id}
                      entry={e}
                      mine={e.userId === me}
                      team={team}
                      overlap={overlaps.has(e.id)}
                      canCreate={canEdit}
                      onOpen={() => setEditing(e)}
                      onDuplicate={() => setDuplicating(e)}
                      onDelete={() => void archive.run(e)}
                    />
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </Week>
      ))}
      {data.truncated && (
        <p className="text-caption text-fg-muted">Pokazujemy najnowsze wpisy. Zawęź zakres, żeby zobaczyć resztę.</p>
      )}
      {!ownRange && !data.truncated && (
        <div className="flex justify-center">
          <Button
            variant="ghost"
            size="s"
            className={TOUCH_BUTTON}
            onClick={() => setExtraSteps((n) => n + 1)}
            loading={list.isFetching}
          >
            Pokaż wcześniejsze
          </Button>
        </div>
      )}
      <AddTimeDialog open={editing !== null} onOpenChange={(open) => !open && setEditing(null)} entry={editing} />
      <AddTimeDialog
        open={duplicating !== null}
        onOpenChange={(open) => !open && setDuplicating(null)}
        duplicateOf={duplicating}
      />
    </div>
  );
}

function EntryRow({
  entry: e,
  mine,
  team,
  overlap,
  canCreate,
  onOpen,
  onDuplicate,
  onDelete,
}: {
  entry: TimeEntryDto;
  /** własny wpis: „Moja kwota” (stawka partnera), DECISIONS 2026-10-08 [P] „Stawka partnera” */
  mine: boolean;
  team: boolean;
  overlap: boolean;
  canCreate: boolean;
  onOpen: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}) {
  const range = `${e.start} – ${e.end}${e.endDate ? ' (+1)' : ''}`;
  const canDelete = e.canEdit && !e.billingDraftId;
  const description = e.description || 'Bez opisu';
  const myPay = mine && e.partnerRate ? (e.partnerAmount ?? '0.00') : null;
  const myPayTitle =
    myPay && e.partnerRate
      ? `Moja stawka ${formatRate(e.partnerRate, 'PLN')} · Moja kwota ${formatMoneyAuto(myPay)}${e.partnerSettlementId ? ' · rozliczone z prowadzącym' : ''}`
      : undefined;
  return (
    <li className="flex items-center gap-1 border-b border-line">
      <button
        type="button"
        onClick={onOpen}
        aria-label={`${description}, ${range}, ${formatClock(e.durationSeconds)}${e.canEdit ? ' — edytuj' : ''}`}
        className="flex min-h-[52px] min-w-0 flex-1 flex-col items-stretch gap-0.5 py-2 pl-4 text-left transition-colors hover:bg-surface focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent md:flex-row md:items-center md:gap-4"
      >
        <span className="flex min-w-0 items-center gap-2 md:w-[260px] md:shrink-0">
          <span
            title={description}
            className={cn(
              'text-body-s font-medium break-words max-md:line-clamp-2 md:truncate',
              e.description ? 'text-fg' : 'text-fg-muted',
            )}
          >
            {description}
          </span>
        </span>
        {/* lifting 17.10.2026: klient i projekt jako chipy (klient w swojej barwie) */}
        <span className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5 text-body-s md:flex-nowrap">
          {e.client || e.project ? (
            <>
              {e.client && <ClientChip client={e.client} />}
              {e.project && <ProjectChip name={e.project.name} />}
            </>
          ) : (
            <span className="truncate text-fg-muted">Bez klienta</span>
          )}
        </span>
        {/* telefon: godziny od–do w osobnej linii — klient w swojej mieści się w całości (przegląd Fali 9) */}
        <Mono data-entry-range="" className="text-caption text-fg-secondary md:hidden">
          {range}
        </Mono>
        {team && e.user && (
          <span className="hidden shrink-0 md:inline-flex">
            <Avatar initials={e.user.initials} color={e.user.avatarColor} title={e.user.name} />
          </span>
        )}
        {(overlap || e.closed) && (
          <span className="hidden shrink-0 items-center gap-1.5 md:inline-flex">
            {overlap && (
              <span title="Nachodzi na inny wpis" className="inline-flex text-warning">
                <TriangleAlert size={14} strokeWidth={1.75} aria-hidden />
                <span className="sr-only">Nachodzi na inny wpis</span>
              </span>
            )}
            {e.closed && (
              <span title="Zamknięty w rozliczeniu" className="inline-flex text-fg-muted">
                <Lock size={14} strokeWidth={1.75} aria-hidden />
                <span className="sr-only">Zamknięty w rozliczeniu</span>
              </span>
            )}
          </span>
        )}
        <Mono className="hidden shrink-0 text-caption text-fg-secondary md:inline">{range}</Mono>
      </button>
      <span className="flex shrink-0 items-center gap-1 pr-1 md:gap-2">
        {overlap && (
          <span className="inline-flex text-warning md:hidden" title="Nachodzi na inny wpis">
            <TriangleAlert size={14} strokeWidth={1.75} aria-hidden />
          </span>
        )}
        {myPay && (
          <span
            className="hidden w-20 shrink-0 items-center justify-end gap-1 text-caption text-fg-muted sm:inline-flex"
            title={myPayTitle}
          >
            {e.partnerSettlementId && <CircleCheck size={14} strokeWidth={1.75} aria-hidden />}
            <Mono aria-label={myPayTitle}>{formatMoneyAuto(myPay)}</Mono>
          </span>
        )}
        <Mono className="w-12 text-right text-body-s font-semibold text-fg">{formatClock(e.durationSeconds)}</Mono>
        <Menu>
          <Tooltip content="Więcej">
            <MenuTrigger asChild>
              <IconButton icon={MoreHorizontal} label={`Więcej: ${description}`} className="max-md:size-11" />
            </MenuTrigger>
          </Tooltip>
          <MenuContent align="end">
            <MenuItem icon={Pencil} onSelect={() => setTimeout(onOpen, 0)}>
              {e.canEdit ? 'Edytuj' : 'Szczegóły'}
            </MenuItem>
            {canCreate && (
              <MenuItem icon={Copy} onSelect={() => setTimeout(onDuplicate, 0)}>
                Duplikuj
              </MenuItem>
            )}
            {canDelete && (
              <>
                <MenuSeparator />
                <MenuItem icon={Trash2} tone="danger" onSelect={onDelete}>
                  Usuń
                </MenuItem>
              </>
            )}
          </MenuContent>
        </Menu>
      </span>
    </li>
  );
}
